import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import '../../styles/FactionRosterExperience.css';
import ChooseMode from './faction-steps/ChooseMode';
import ChooseIndependentWrestlers from './faction-steps/ChooseIndependentWrestlers';
import FactionSummary from './faction-steps/FactionSummary';
import CreateRosterForm from './faction-steps/CreateRosterForm';
import CreatePublicRosterForm from './faction-steps/CreatePublicRosterForm';
import SeasonCreationBanner from '../SeasonCreationBanner';
import { getFactionWrestlerIds } from '../../utils/factionWrestlers';
import { canCreateFaction } from '../../utils/seasonGate';

export default function CreateFactionFlow({
  onClose,
  userProfile,
  onRoadTo,
  skipInitialStep = false,
  existingFactionId = null,
  defaultRoster = null,
}) {
  const [mode, setMode] = useState(skipInitialStep ? 'indipendente' : null);
  const [selectedWrestlers, setSelectedWrestlers] = useState([]);
  const [step, setStep] = useState(skipInitialStep ? 2 : 1);

  const [independentSubStep, setIndependentSubStep] = useState('chooseType');
  const [privateFactionCode, setPrivateFactionCode] = useState('');

  const [competitiveSubStep, setCompetitiveSubStep] = useState('join');

  const [availableWrestlers, setAvailableWrestlers] = useState([]);
  const [loadingWrestlers, setLoadingWrestlers] = useState(true);
  const [joinCode, setJoinCode] = useState('');
  const [rosterName, setRosterName] = useState('');
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [latestPublicRosterId, setLatestPublicRosterId] = useState(null);
  const [latestPublicRosterName, setLatestPublicRosterName] = useState('');
  const [publicRosterBlockNotice, setPublicRosterBlockNotice] = useState('');

  const isForcedCreation = (skipInitialStep && defaultRoster) || (skipInitialStep && existingFactionId);
  const isCreatingNewFaction = !existingFactionId;
  const canUserCreateFaction = canCreateFaction(userProfile?.email);

  const checkUserFactionInRoster = async (rosterId) => {
    if (!userProfile?.uid || !rosterId) return false;
    try {
      const factionsQuery = query(
        collection(db, 'factions'),
        where('ownerUid', '==', userProfile.uid),
        where('rosterId', '==', rosterId)
      );
      const snapshot = await getDocs(factionsQuery);
      return !snapshot.empty;
    } catch (error) {
      console.error('Errore verifica fazione esistente:', error);
      return false;
    }
  };

  useEffect(() => {
    async function loadData() {
      setLoadingWrestlers(true);
      setPublicRosterBlockNotice('');
      try {
        const wrestlersSnapshot = await getDocs(collection(db, 'wrestlers'));
        const wrestlerList = wrestlersSnapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));

        wrestlerList.sort((first, second) => first.name.localeCompare(second.name));
        setAvailableWrestlers(wrestlerList);

        if (skipInitialStep && defaultRoster) {
          setSelectedRoster(defaultRoster);
          setRosterName(defaultRoster.name);
          setIndependentSubStep('chooseWrestlers');
          setLoadingWrestlers(false);
          return;
        }

        const rosterSnapshot = await getDocs(query(collection(db, 'rosters'), orderBy('createdAt', 'desc')));
        const indyRosters = rosterSnapshot.docs
          .map((document) => ({ id: document.id, ...document.data() }))
          .filter((roster) => roster.gameType === 'Indipendente');

        const publicRosters = indyRosters.filter(
          (roster) => roster.private !== true && roster.isPublished !== false
        );
        setLatestPublicRosterId(publicRosters[0]?.id || null);
        setLatestPublicRosterName(publicRosters[0]?.name || '');

        let defaultRosterAuto = null;
        for (const roster of publicRosters) {
          const hasFaction = await checkUserFactionInRoster(roster.id);
          if (!hasFaction) {
            defaultRosterAuto = roster;
            break;
          }
        }

        if (defaultRosterAuto) {
          setRosterName(defaultRosterAuto.name);
          setSelectedRoster(defaultRosterAuto);
        } else if (publicRosters.length > 0) {
          setRosterName(publicRosters[0].name);
          setSelectedRoster(null);
          setPublicRosterBlockNotice(
            `Hai gia una fazione nel roster pubblico "${publicRosters[0].name}". Nei roster indipendenti pubblici non si possono creare due fazioni nello stesso roster.`
          );
        }

        if (existingFactionId) {
          try {
            const factionSnapshot = await getDoc(doc(db, 'factions', existingFactionId));
            if (factionSnapshot.exists()) {
              const factionData = factionSnapshot.data();
              const selected = getFactionWrestlerIds(factionData)
                .map((id) => wrestlerList.find((wrestler) => wrestler.id === id))
                .filter(Boolean);

              setSelectedWrestlers(selected);
              setMode('indipendente');
              setStep(2);
              setIndependentSubStep('chooseWrestlers');

              if (factionData.type === 'private') setPrivateFactionCode(factionData.code || '');
              if (factionData.roster) {
                setRosterName(factionData.roster);
                const existingRoster = indyRosters.find((roster) => roster.name === factionData.roster);
                if (existingRoster) setSelectedRoster(existingRoster);
              }
            }
          } catch (error) {
            console.error('Errore caricando la fazione esistente:', error);
          }
        }
      } catch (error) {
        console.error('Errore nel caricamento dei dati:', error);
      } finally {
        setLoadingWrestlers(false);
      }
    }

    loadData();
  }, [skipInitialStep, defaultRoster, existingFactionId]);

  const handleModeSelected = (selectedMode) => {
    setMode(selectedMode);
    if (selectedMode === 'indipendente') {
      setIndependentSubStep('chooseType');
      setStep(2);
    } else {
      setCompetitiveSubStep('join');
      setStep(2);
    }
  };

  const handleJoinRoster = () => {
    if (userProfile?.email) {
      setJoinCode(userProfile.email);
      setStep(4);
    } else {
      alert('Errore: Profilo utente non disponibile');
    }
  };

  const handleCreateRoster = () => {
    setCompetitiveSubStep('form');
  };

  const copyJoinCode = async () => {
    try {
      await navigator.clipboard.writeText(joinCode);
      alert('Codice copiato! Inviarlo al proprietario del roster.');
    } catch (error) {
      console.error('Errore nella copia del codice:', error);
      alert('Impossibile copiare il codice');
    }
  };

  const handleIndependentConfirm = (selected) => {
    setSelectedWrestlers(selected);
    setStep(3);
  };

  const handleFactionCreated = () => {
    window.location.reload();
  };

  const handleClose = () => {
    window.location.reload();
  };

  const handleCardKeyDown = (event, action, disabled = false) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const handleBack = () => {
    if (isForcedCreation) return;

    if (step === 1) {
      handleClose();
      return;
    }

    if (step === 4) {
      setStep(2);
      setCompetitiveSubStep('join');
      return;
    }

    if (step === 3 && mode === 'indipendente') {
      setStep(2);
      setIndependentSubStep('chooseWrestlers');
      return;
    }

    if (step === 2) {
      if (mode === 'competitivo') {
        if (competitiveSubStep === 'form') {
          setCompetitiveSubStep('join');
        } else if (competitiveSubStep === 'join') {
          setStep(1);
          setMode(null);
        }
        return;
      }

      if (mode === 'indipendente') {
        switch (independentSubStep) {
          case 'chooseWrestlers':
            setIndependentSubStep('enterOrCreate');
            break;
          case 'createRoster':
            setIndependentSubStep('enterOrCreate');
            setPrivateFactionCode('');
            break;
          case 'enterOrCreate':
            setIndependentSubStep('chooseType');
            setPrivateFactionCode('');
            break;
          case 'chooseType':
            setStep(1);
            setMode(null);
            break;
          default:
            setStep(1);
            setMode(null);
        }
        return;
      }
    }

    if (step > 1) {
      setStep(step - 1);
    } else {
      handleClose();
    }
  };

  const SelectionCard = ({
    icon,
    title,
    description,
    onClick,
    disabled = false,
    badge = null,
    helper = null,
    iconClassName = 'text-brand-secondary',
    cardStyle = {},
  }) => (
    <article
      className={`card h-100 border-0 shadow-sm frx-auction-card ${disabled ? 'opacity-50' : ''}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onClick()}
      onKeyDown={(event) => handleCardKeyDown(event, onClick, disabled)}
      aria-disabled={disabled}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        ...cardStyle,
      }}
    >
      <div className="card-body text-center p-4 p-lg-5">
        <div
          className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
          style={{
            width: '68px',
            height: '68px',
            background: 'rgba(255,255,255,0.08)',
          }}
        >
          <i className={`bi ${icon} fs-2 ${iconClassName}`}></i>
        </div>
        <h5 className="brand-title mb-2">{title}</h5>
        <div className="text-muted-brand mb-3">{description}</div>
        {helper ? <div className="frx-helper mb-3">{helper}</div> : null}
        {badge ? <div className="frx-pill-row justify-content-center">{badge}</div> : null}
      </div>
    </article>
  );

  const ChooseIndependentType = () => {
    const [checkingPublicRoster, setCheckingPublicRoster] = useState(false);
    const [publicRosterError, setPublicRosterError] = useState('');

    const isUserAlreadyInLatestPublicRoster = () => {
      if (!userProfile?.rosterIds || !latestPublicRosterId) return false;
      return userProfile.rosterIds.includes(latestPublicRosterId);
    };

    const isPublicRosterBlocked =
      isUserAlreadyInLatestPublicRoster() || Boolean(publicRosterBlockNotice);
    const publicRosterWarning =
      publicRosterBlockNotice ||
      (isUserAlreadyInLatestPublicRoster()
        ? `Hai gia una fazione nel roster pubblico "${latestPublicRosterName || rosterName}". Nei roster indipendenti pubblici non si possono creare due fazioni nello stesso roster.`
        : '');

    const handlePublic = async () => {
      if (!selectedRoster) {
        setPublicRosterError('Nessun roster disponibile');
        return;
      }

      if (isUserAlreadyInLatestPublicRoster()) {
        setPublicRosterError('Sei già membro dell’ultimo roster pubblico creato. Non puoi unirti nuovamente.');
        return;
      }

      setCheckingPublicRoster(true);
      setPublicRosterError('');

      try {
        const hasFaction = await checkUserFactionInRoster(selectedRoster.id);
        if (hasFaction) {
          setPublicRosterError(`Hai già una fazione nel roster "${selectedRoster.name}". Non puoi crearne un'altra.`);
          setCheckingPublicRoster(false);
          return;
        }
        setIndependentSubStep('chooseWrestlers');
      } catch (error) {
        console.error('Errore:', error);
        setPublicRosterError('Errore durante la verifica');
      } finally {
        setCheckingPublicRoster(false);
      }
    };

    const handlePrivate = () => {
      setIndependentSubStep('enterOrCreate');
    };

    return (
      <div className="p-4">
        {publicRosterWarning && (
          <div className="alert alert-warning mb-3 bg-warning bg-opacity-10 text-light border-0">
            <i className="bi bi-info-circle-fill me-2"></i>
            {publicRosterWarning}
          </div>
        )}
        {publicRosterError && (
          <div className="alert alert-danger mb-3 bg-danger bg-opacity-20 text-light border-0">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {publicRosterError}
          </div>
        )}
        <div className="row g-4">
          <div className="col-md-6">
            <SelectionCard
              icon="bi-globe"
              title="Pubblico"
              description={(
                <>
                  Entra a far parte del roster <strong>{rosterName}</strong> creato per il prossimo PLE.
                </>
              )}
              onClick={handlePublic}
              disabled={checkingPublicRoster || isPublicRosterBlocked}
              helper={
                isPublicRosterBlocked
                  ? 'Ogni utente puo avere una sola fazione per roster indipendente pubblico.'
                  : null
              }
              cardStyle={{ background: 'linear-gradient(180deg, rgba(10, 20, 30, 0.94), rgba(8, 14, 24, 0.9))' }}
            />
          </div>
          <div className="col-md-6">
            <SelectionCard
              icon="bi-lock"
              title="Privato"
              description="Unisciti a un roster privato o creane uno nuovo."
              onClick={handlePrivate}
              cardStyle={{ background: 'linear-gradient(180deg, rgba(10, 20, 30, 0.94), rgba(8, 14, 24, 0.9))' }}
            />
          </div>
        </div>
      </div>
    );
  };

  const ChoosePrivateAction = () => (
    <div className="text-center">
      <div className="row g-4 mt-2">
        <div className="col-md-6">
          <SelectionCard
            icon="bi-link"
            title="Unisciti ad un roster privato"
            description="Richiedi il link di invito al proprietario del roster"
            onClick={() => {}}
            cardStyle={{ background: 'linear-gradient(180deg, rgba(10, 20, 30, 0.94), rgba(8, 14, 24, 0.9))' }}
          />
        </div>
        <div className="col-md-6">
          <SelectionCard
            icon="bi-plus-circle"
            title="Crea nuovo roster"
            description="Crea un nuovo roster indipendente privato"
            onClick={() => setIndependentSubStep('createRoster')}
            cardStyle={{ background: 'linear-gradient(180deg, rgba(10, 20, 30, 0.94), rgba(8, 14, 24, 0.9))' }}
          />
        </div>
      </div>
    </div>
  );

  const CreateRosterStep = () => {
    const handleRosterComplete = () => {
      window.location.reload();
    };

    return (
      <CreatePublicRosterForm
        gameType="Indipendente"
        onComplete={handleRosterComplete}
        onBack={() => setIndependentSubStep('enterOrCreate')}
      />
    );
  };

  const CompetitiveJoinStep = () => (
    <div className="text-center">
      <div className="row g-4 mt-2">
        <div className="col-md-6">
          <SelectionCard
            icon="bi-person-plus-fill"
            title="Unisciti a un roster"
            description="Richiedi un invito per entrare in un roster competitivo esistente"
            onClick={handleJoinRoster}
            cardStyle={{ background: 'linear-gradient(180deg, rgba(10, 20, 30, 0.94), rgba(8, 14, 24, 0.9))' }}
          />
        </div>
        <div className="col-md-6">
          <SelectionCard
            icon="bi-plus-circle-fill"
            title="Crea un nuovo roster"
            description="Crea un nuovo roster competitivo e gestisci membri e regole"
            onClick={handleCreateRoster}
            cardStyle={{ background: 'linear-gradient(180deg, rgba(10, 20, 30, 0.94), rgba(8, 14, 24, 0.9))' }}
          />
        </div>
      </div>
    </div>
  );

  const CompetitiveSummaryStep = () => (
    <div className="text-center">
      <div className="frx-surface rounded-4 p-4 p-lg-5">
        <div className="mb-4">
          <i className="bi bi-envelope-paper fs-1" style={{ color: '#3498db' }}></i>
        </div>
        <h5 className="brand-title mb-2">Unisciti a un roster</h5>
        <p className="text-muted-brand mb-4">Condividi questa email con il proprietario del roster.</p>
        <div className="frx-copy-box mb-4">
          <div className="d-flex align-items-center justify-content-center gap-2">
            <div className="flex-grow-1 px-3 py-3 rounded text-light" style={{ fontSize: '1rem', letterSpacing: '0.5px' }}>
              {joinCode}
            </div>
            <button className="btn btn-outline-primary" onClick={copyJoinCode} type="button">
              <i className="bi bi-copy"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderIndependent = () => {
    switch (independentSubStep) {
      case 'chooseType':
        return <ChooseIndependentType />;
      case 'enterOrCreate':
        return <ChoosePrivateAction />;
      case 'createRoster':
        return <CreateRosterStep />;
      case 'chooseWrestlers':
        return (
          <ChooseIndependentWrestlers
            availableWrestlers={availableWrestlers}
            loading={loadingWrestlers}
            rosterName={rosterName}
            roster={selectedRoster}
            initialSelection={selectedWrestlers}
            onConfirm={handleIndependentConfirm}
            onBack={isForcedCreation ? undefined : handleBack}
            onCancel={handleClose}
            disableBack={isForcedCreation}
          />
        );
      default:
        return null;
    }
  };

  const renderCompetitive = () => {
    if (competitiveSubStep === 'join') {
      return <CompetitiveJoinStep />;
    }
    if (competitiveSubStep === 'form') {
      return (
        <CreateRosterForm
          onBack={() => setCompetitiveSubStep('join')}
          onComplete={handleClose}
          ownerId={userProfile?.uid}
        />
      );
    }
    return null;
  };

  const getTitle = () => {
    if (step === 1) return 'Scegli Modalità';
    if (step === 4) return 'Email per la partecipazione';
    if (step === 3 && mode === 'indipendente') return 'Riepilogo';
    if (step === 2 && mode === 'indipendente') {
      if (independentSubStep === 'chooseType') return 'Scegli Tipo Roster';
      if (independentSubStep === 'enterOrCreate') return 'Unisciti o Crea Roster';
      if (independentSubStep === 'createRoster') return 'Crea Nuovo Roster';
      if (independentSubStep === 'chooseWrestlers') return 'Seleziona Wrestler';
    }
    if (step === 2 && mode === 'competitivo') {
      if (competitiveSubStep === 'join') return 'Modalità Competitiva';
      if (competitiveSubStep === 'form') return 'Crea Roster';
    }
    return '';
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.76)', backdropFilter: 'blur(8px)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content p-0 bg-brand-dark frx-surface" style={{ borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(194,157,84,0.3)' }}>
          <div className="modal-header border-0 px-4 py-3" style={{ borderBottom: '1px solid rgba(194,157,84,0.2)' }}>
            <div>
              <h5 className="modal-title frx-kicker mb-0">{getTitle()}</h5>
            </div>
            <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={handleClose} />
          </div>
          <div className="modal-body p-0">
            {isCreatingNewFaction && !canUserCreateFaction ? (
              <div className="p-4">
                <SeasonCreationBanner userEmail={userProfile?.email} />
              </div>
            ) : (
              <>
                {step === 1 && <ChooseMode onSelect={handleModeSelected} onCancel={handleClose} onRoadTo={onRoadTo} />}
                {step === 2 && mode === 'indipendente' && renderIndependent()}
                {step === 2 && mode === 'competitivo' && renderCompetitive()}
                {step === 3 && mode === 'indipendente' && (
                  <FactionSummary
                    selectedWrestlers={selectedWrestlers}
                    gameMode="Indipendente"
                    onBack={handleBack}
                    userProfile={userProfile}
                    onComplete={handleFactionCreated}
                    existingFactionId={existingFactionId}
                    independentType={independentSubStep === 'chooseWrestlers' ? (privateFactionCode ? 'private' : 'public') : 'public'}
                    privateCode={privateFactionCode}
                    rosterName={rosterName}
                    roster={selectedRoster}
                  />
                )}
                {step === 4 && <CompetitiveSummaryStep />}
              </>
            )}
          </div>
          <div className="modal-footer border-0 px-4 py-3" style={{ borderTop: '1px solid rgba(194,157,84,0.2)' }}>
            <div className="d-flex justify-content-between w-100">
              {!isForcedCreation && step !== 4 && (
                <button className="btn btn-outline-brand-secondary" onClick={handleBack} type="button">
                  <i className="bi bi-arrow-left me-2"></i>
                  {step === 1 ? 'Annulla' : 'Indietro'}
                </button>
              )}
              {step === 4 && (
                <button className="btn btn-brand-secondary ms-auto" onClick={handleClose} type="button">
                  Chiudi <i className="bi bi-check-lg ms-2"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
